<script setup lang="ts">
import type { MDBCulture, MDBCultureVariété } from '@/types/ferme/culture'

const storeU = useUtilisateurStore()
const dialog = ref(false)
const isVariété = ref(false)
const dialogVariété = ref(false)
const isLoading = ref(true)
const selected = ref('')
const cultures = ref([] as MDBCulture[])
const culturesWhitelist = ref([] as string[])
const variétés = ref([] as MDBCultureVariété[])

let resolve: (value: string) => void

watch(dialog, (v) => {
  if (!v) {
    resolve(selected.value)
  }
})

function open() {
  if (!storeU.siteSélectionné) {
    Notify.error('Veuillez sélectionner un site')
    return
  }

  selected.value = ''
  dialog.value = true
  selectCulturesToShow()
  return new Promise<string>((resolveFn) => {
    resolve = resolveFn
  })
}

function openVariété() {
  isVariété.value = true
  return open()
}

function openFew(culturesToShow: string[]) {
  culturesWhitelist.value = culturesToShow
  return open()
}

function openVariétéFew(culturesToShow: string[]) {
  isVariété.value = true
  culturesWhitelist.value = culturesToShow
  return open()
}

async function selectCulturesToShow() {
  isLoading.value = true

  if (storeU.cultures.length == 0) {
    try {
      const siteCS = storeU.siteSélectionné.lettresDappel
      const response = await apiProphet.get(`sites/${siteCS}/cultures`)
      const culturesFetch = response.data as MDBCulture[]
      storeU.cultures = culturesFetch.sort((a, b) => a.nom.localeCompare(b.nom))
      selectCulturesToShow()
    } catch (error) {
      Notify.error('Impossible de récupérer les cultures\n' + error)
      return
    }
  }

  if (culturesWhitelist.value.length > 0) {
    cultures.value = storeU.cultures.filter((culture) => culturesWhitelist.value.includes(culture.nom))
  } else {
    cultures.value = storeU.cultures
  }

  isLoading.value = false
}

function confirm(culture: MDBCulture) {
  selected.value = culture.nom

  if (isVariété.value) {
    variétés.value = culture.variétés.sort((a, b) => a.nom.localeCompare(b.nom))
    dialogVariété.value = true
  } else {
    dialog.value = false
  }
}

function cancel() {
  selected.value = ''
  dialog.value = false
}

function confirmVariété(variété: MDBCultureVariété) {
  selected.value = `${selected.value}:${variété.nom}`
  dialogVariété.value = false
  dialog.value = false
}

function cancelVariété() {
  dialogVariété.value = false
}

defineExpose({ open, openFew, openVariété, openVariétéFew })
</script>

<template>
  <v-dialog v-model="dialog" max-width="800px" :fullscreen="$vuetify.display.xs">
    <v-card style="z-index: -1">

      <v-card-title class="d-flex justify-space-between align-center">
        <span class="text-h5">Choisir la culture</span>
        <v-btn icon="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" variant="text" @click="cancel"/>
      </v-card-title>

      <v-divider class="mb-4"/>

      <v-card-text>
        <v-row>
          <div v-if="isLoading" class="my-2 d-flex align-center mr-2">
            <v-progress-circular :indeterminate="true" color="primary" size="24" width="2" class="ma-2"/>
            <span class="text-medium-emphasis">Chargement des cultures</span>
          </div>
          <v-col v-for="culture in cultures" :key="culture.id" cols="12" sm="6">
            <v-btn class="text-capitalize" color="primary" block @click="confirm(culture)">{{ culture.nom }}</v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider/>

      <v-card-actions class="my-2 d-flex justify-end">
        <v-btn @click="cancel">Annuler</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog v-model="dialogVariété" max-width="800px">
    <v-card style="z-index: -1">

      <v-card-title class="d-flex justify-space-between align-center">
        <span class="text-h5">Choisir la variété</span>
        <v-btn icon="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" variant="text" @click="cancelVariété"/>
      </v-card-title>

      <v-divider class="mb-4"/>

      <v-card-text>
        <v-row>
          <v-col v-for="variété in variétés" :key="variété.nom" cols="12" md="6">
            <v-btn class="text-capitalize" color="primary" block @click="confirmVariété(variété)">{{ variété.nom }}</v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider/>

      <v-card-actions class="my-2 d-flex justify-end">
        <v-btn @click="cancelVariété">Annuler</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
